import { DraftIncomeSource } from 'src/app/models/income-wizard-workflow';

// StreamType sort order for wizard walkthrough
const STEP_TYPE_ORDER: Record<number, number> = {
  3: 0, // WorkNumber first
  0: 1, // PayrollSync
  1: 2, // BankSync
  2: 3, // DocUpload last
};

export interface WizardStep {
  groupKey: string;
  streamType: number;
  subType: number;
  drafts: DraftIncomeSource[];
  isDecided: boolean;
  label: string;
}

/**
 * Groups drafts into wizard steps.
 * - BankSync: grouped by account prefix (everything before the last ":" segment)
 * - DocUpload paystubs (subType 3): grouped by "paystubs" prefix
 * - All others: one draft = one step
 */
export function buildWizardSteps(drafts: DraftIncomeSource[]): WizardStep[] {
  const groups = new Map<string, DraftIncomeSource[]>();

  for (const d of drafts) {
    const key = groupKeyForDraft(d);
    const list = groups.get(key) ?? [];
    list.push(d);
    groups.set(key, list);
  }

  const steps: WizardStep[] = [];
  for (const [key, groupDrafts] of groups) {
    const first = groupDrafts[0];
    steps.push({
      groupKey: key,
      streamType: first.streamType,
      subType: first.subType,
      drafts: groupDrafts,
      isDecided: groupDrafts.every((d) => d.status.toLowerCase() !== 'pending'),
      label: stepLabel(first),
    });
  }

  steps.sort((a, b) => {
    const oa = STEP_TYPE_ORDER[a.streamType] ?? 99;
    const ob = STEP_TYPE_ORDER[b.streamType] ?? 99;
    if (oa !== ob) return oa - ob;
    return a.groupKey.localeCompare(b.groupKey);
  });

  return steps;
}

function groupKeyForDraft(d: DraftIncomeSource): string {
  // BankSync: group by account prefix (drop last segment after ":")
  if (d.streamType === 1) {
    const lastColon = d.streamIdentifier.lastIndexOf(':');
    return lastColon > 0 ? d.streamIdentifier.substring(0, lastColon) : d.streamIdentifier;
  }
  // Everything else (including paystubs): each draft is its own step
  return d.streamIdentifier;
}

function stepLabel(d: DraftIncomeSource): string {
  switch (d.streamType) {
    case 0: return `Payroll: ${d.employerName || 'Unknown'}`;
    case 1: return `Bank Account`;
    case 2: return `Document: ${d.employerName || 'Upload'}`;
    case 3: return `Work Number: ${d.employerName || 'Unknown'}`;
    default: return d.employerName || 'Income Source';
  }
}
